'use client';

import React, { useState, useEffect, useCallback } from 'react';
import Image from 'next/image';
// import Link from 'next/link';
// import { useTranslations } from 'next-intl';
import get from 'lodash/get';
import { BASE_FILE_URL } from '@/common/constants';

const CHUNK_SIZE = 5;

const PromoteTourism = ({ tourismData }) => {
  // const t = useTranslations('');
  const items = get(tourismData, 'results', []);

  // Chia items thành các slide, mỗi slide 5 ảnh
  const slides = [];
  for (let i = 0; i < items.length; i += CHUNK_SIZE) {
    slides.push(items.slice(i, i + CHUNK_SIZE));
  }

  const [current, setCurrent] = useState(0);
  const [isAnimating, setIsAnimating] = useState(false);

  const goTo = useCallback(
    (index) => {
      if (isAnimating || index === current || slides.length <= 1) return;
      setIsAnimating(true);
      setCurrent(index);
      setTimeout(() => setIsAnimating(false), 600);
    },
    [isAnimating, current, slides.length],
  );

  const prev = () => goTo((current - 1 + slides.length) % slides.length);
  const next = useCallback(() => goTo((current + 1) % slides.length), [current, goTo, slides.length]);

  useEffect(() => {
    if (slides.length <= 1) return;
    const timer = setInterval(next, 6000);
    return () => clearInterval(timer);
  }, [next, slides.length]);

  if (!items.length) return null;

  const TAG_LABELS = ['Điểm đến', 'Cảnh quan', 'Văn hóa', 'Lễ hội', 'Trải nghiệm'];
  return (
    <section className="relative py-12 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-gradient-to-br from-[#1a3c34]/90 via-[#0d2b26]/92 to-[#1a3c34]/90 z-0" />
      <div className="absolute inset-0 bg-[url('/bg-canh-dep.jpg')] bg-cover bg-center bg-fixed -z-10" />

      <div className="relative z-10 max-w-screen-xl mx-auto px-4">
        {/* Header */}
        <div className="mb-2 pb-3 border-b border-white/20">
          <h2 className="text-white text-xl font-bold uppercase flex items-center gap-2">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round">
              <path d="m8 3 4 8 5-5 5 15H2L8 3z" />
            </svg>
            Khám Phá Bằng Lang
          </h2>
        </div>
        <p className="text-white/70 text-sm mb-8">Vùng đất giàu bản sắc văn hóa dân tộc, thiên nhiên hoang sơ và hùng vĩ</p>

        {/* Slider */}
        <div className="relative">
          {slides.map((slideItems, slideIndex) => (
            <div
              key={slideIndex}
              className={`transition-all duration-600 ${slideIndex === current ? 'block opacity-100' : 'hidden opacity-0'}`}
              style={{
                animation: slideIndex === current ? 'tourismFadeIn 0.6s ease forwards' : undefined,
              }}>
              <div className="grid grid-cols-3 grid-rows-2 gap-4">
                {slideItems.map((item, index) => {
                  const isLarge = index === 0;
                  const tag = item.tag || TAG_LABELS[index % TAG_LABELS.length];
                  // const href = websiteCode
                  //   ? `/sub/${websiteCode}/${item?.menu?.slug || 'kham-pha'}/${item.slug}`
                  //   : `/${item?.menu?.slug || 'kham-pha'}/${item.slug}`;
                  return (
                    <div
                      key={item.id || item.slug}
                      className={`relative overflow-hidden rounded-lg block group ${isLarge ? 'col-span-1 row-span-2' : 'aspect-[4/3]'}`}>
                      <Image
                        src={`${BASE_FILE_URL}/${item.avatarUrl}`}
                        alt={item.title || item.name || ''}
                        fill
                        className="object-cover group-hover:scale-105 transition-transform duration-400 position-initial"
                        quality={85}
                      />

                      {/* Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/10 to-transparent group-hover:from-black/90 group-hover:via-black/20 transition-all duration-300 flex flex-col justify-end p-4">
                        <span className="inline-block self-start bg-[#ffd600] text-[#0d2b26] text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded mb-2">
                          {tag}
                        </span>
                        <h3 className={`text-white font-bold leading-snug line-clamp-2 ${isLarge ? 'text-[18px]' : 'text-[13px]'}`}>
                          {item.title || item.name}
                        </h3>
                        {isLarge && item.summary && <p className="text-white/70 text-[13px] mt-1.5 line-clamp-2">{item.summary}</p>}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Prev / Next */}
          {slides.length > 1 && (
            <>
              <button
                onClick={prev}
                aria-label="Previous"
                className="absolute left-0 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 border border-white/25 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-colors duration-200 z-10">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
              <button
                onClick={next}
                aria-label="Next"
                className="absolute right-0 top-1/2 -translate-y-1/2 w-11 h-11 rounded-full bg-white/15 border border-white/25 backdrop-blur-sm text-white flex items-center justify-center hover:bg-white/30 transition-colors duration-200 z-10">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round">
                  <polyline points="9 18 15 12 9 6" />
                </svg>
              </button>
            </>
          )}
        </div>

        {/* Dots */}
        {slides.length > 1 && (
          <div className="flex justify-center gap-2.5 mt-6">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goTo(index)}
                aria-label={`Slide ${index + 1}`}
                className={`rounded-full transition-all duration-300 ${
                  index === current ? 'w-6 h-2.5 bg-[#ffd600]' : 'w-2.5 h-2.5 bg-white/35 hover:bg-white/60'
                }`}
              />
            ))}
          </div>
        )}

        {/* CTA */}
        {/* <div className="text-center mt-8">
          <Link
            href={websiteCode ? `/sub/${websiteCode}/kham-pha` : '/kham-pha'}
            className="inline-flex items-center gap-2.5 bg-[#ffd600] text-[#0d2b26] px-8 py-3.5 rounded-full font-bold text-[15px] hover:bg-white hover:-translate-y-0.5 transition-all duration-300 shadow-[0_4px_15px_rgba(255,214,0,0.3)] hover:shadow-[0_6px_20px_rgba(255,255,255,0.3)]">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round">
              <circle cx="12" cy="12" r="10" />
              <polygon points="16.24 7.76 14.12 14.12 7.76 16.24 9.88 9.88 16.24 7.76" />
            </svg>
            Khám phá thêm điểm đến
          </Link>
        </div> */}
      </div>

      <style>{`
        @keyframes tourismFadeIn {
          from {
            opacity: 0;
            transform: translateX(20px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </section>
  );
};

export default PromoteTourism;
